import React, { useEffect, useRef, useState } from 'react';
import { inversionAPI, InvResult } from '../../services/api';

// /inversion/stats — 反演统计图 (复刻 MATLAB 参考: 每段缝长/SRA柱状 + 走向玫瑰 + 缝长分布)
const TH = {
  bg: '#FFFFFF', grid: 'rgba(30,42,58,0.08)', text: '#1E2A3A', textSec: '#4F6680',
  textMuted: '#8598AD', headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  bar: '#2874A6', bar2: '#148F77', accent: '#C2533A',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};
const PANEL: React.CSSProperties = { background: '#fff', border: `1px solid ${TH.headerBorder}`, borderRadius: 4 };
const HDR: React.CSSProperties = { padding: '6px 12px', background: TH.panelHeaderBg, borderBottom: `1px solid ${TH.headerBorder}`, fontSize: 12, fontWeight: 600, color: TH.text };

function BarChart({ labels, values, color, ylabel, meanLine, height = 210 }:
  { labels: string[]; values: number[]; color: string; ylabel: string; meanLine?: boolean; height?: number }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    const W = cv.clientWidth, H = cv.clientHeight;
    cv.width = W * dpr; cv.height = H * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    if (!values.length) return;
    const mL = 46, mR = 10, mT = 10, mB = 24;
    const pw = W - mL - mR, ph = H - mT - mB;
    const vmax = Math.max(...values) * 1.1 || 1;
    ctx.font = `10px ${TH.fontMono}`; ctx.strokeStyle = TH.grid; ctx.fillStyle = TH.textMuted;
    ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
    for (let i = 0; i <= 4; i++) {
      const v = vmax * i / 4, y = mT + ph - (v / vmax) * ph;
      ctx.beginPath(); ctx.moveTo(mL, y); ctx.lineTo(W - mR, y); ctx.stroke();
      ctx.fillText(v >= 100 ? v.toFixed(0) : v.toFixed(1), mL - 4, y);
    }
    const bw = pw / values.length;
    ctx.fillStyle = color;
    values.forEach((v, i) => {
      const h = (v / vmax) * ph;
      ctx.fillRect(mL + i * bw + bw * 0.15, mT + ph - h, bw * 0.7, h);
    });
    ctx.fillStyle = TH.textMuted; ctx.textAlign = 'center'; ctx.textBaseline = 'top';
    const step = Math.ceil(values.length / 13);
    labels.forEach((l, i) => { if (i % step === 0) ctx.fillText(l, mL + i * bw + bw / 2, mT + ph + 4); });
    if (meanLine) {
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const y = mT + ph - (mean / vmax) * ph;
      ctx.strokeStyle = TH.text; ctx.setLineDash([6, 4]); ctx.beginPath();
      ctx.moveTo(mL, y); ctx.lineTo(W - mR, y); ctx.stroke(); ctx.setLineDash([]);
      ctx.textAlign = 'left'; ctx.fillStyle = TH.text;
      ctx.fillText(`均值 ${mean.toFixed(1)}`, mL + 4, y - 12);
    }
    ctx.save(); ctx.translate(11, mT + ph / 2); ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = TH.textMuted; ctx.textAlign = 'center'; ctx.fillText(ylabel, 0, 0); ctx.restore();
  });
  return <canvas ref={ref} style={{ width: '100%', height, display: 'block' }} />;
}

function RoseChart({ strikes, height = 210 }: { strikes: number[]; height?: number }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const cv = ref.current; if (!cv) return;
    const ctx = cv.getContext('2d')!;
    const dpr = window.devicePixelRatio || 1;
    const W = cv.clientWidth, H = cv.clientHeight;
    cv.width = W * dpr; cv.height = H * dpr; ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);
    const cx = W / 2, cy = H / 2 + 6, R = Math.min(W, H) / 2 - 24;
    // 10° 一格, 走向双向对称
    const bins = new Array(18).fill(0);
    strikes.forEach((s) => { bins[Math.floor((((s % 180) + 180) % 180) / 10)] += 1; });
    const bmax = Math.max(...bins) || 1;
    ctx.strokeStyle = TH.grid;
    for (let r = 1; r <= 3; r++) { ctx.beginPath(); ctx.arc(cx, cy, R * r / 3, 0, 2 * Math.PI); ctx.stroke(); }
    ctx.font = `10px ${TH.fontMono}`; ctx.fillStyle = TH.textMuted; ctx.textAlign = 'center';
    ctx.fillText('N', cx, cy - R - 12); ctx.fillText('E', cx + R + 10, cy);
    for (let i = 0; i < 18; i++) {
      const frac = bins[i] / bmax; if (!frac) continue;
      // 走向角从北顺时针; 画双向扇形
      for (const off of [0, 180]) {
        const a0 = ((i * 10 + off) - 90) * Math.PI / 180, a1 = (((i + 1) * 10 + off) - 90) * Math.PI / 180;
        ctx.fillStyle = 'rgba(194,83,58,0.55)';
        ctx.beginPath(); ctx.moveTo(cx, cy);
        ctx.arc(cx, cy, R * frac, a0 - Math.PI / 2 + Math.PI / 2, a1 - Math.PI / 2 + Math.PI / 2);
        ctx.closePath(); ctx.fill();
      }
    }
  });
  return <canvas ref={ref} style={{ width: '100%', height, display: 'block' }} />;
}

export default function InversionStatsPage() {
  const [res, setRes] = useState<InvResult | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    inversionAPI.run().then((r) => setRes(r.data)).catch((e) => setErr(e?.response?.data?.detail || '加载失败'));
  }, []);

  if (err) return <div style={{ padding: 40, color: TH.textSec }}>{err}</div>;
  if (!res) return <div style={{ padding: 40, color: TH.textSec }}>反演计算中…</div>;

  const stageKeys = Object.keys(res.stages);
  const labels = stageKeys.map((k) => k);
  const meanLen = stageKeys.map((k) => {
    const fs = res.stages[k].fractures;
    return fs.length ? fs.reduce((a, f) => a + f.length_m, 0) / fs.length : 0;
  });
  const sra = stageKeys.map((k) => res.stages[k].sra_1e4m2);
  const nfr = stageKeys.map((k) => res.stages[k].metrics.n_fractures);
  const allFr = stageKeys.flatMap((k) => res.stages[k].fractures);
  const strikes = allFr.map((f) => f.strike_deg);
  // 缝长直方图 (25m 一格)
  const lens = allFr.map((f) => f.length_m);
  const lmax = Math.max(...lens, 1);
  const nb = Math.ceil(lmax / 25);
  const hist = new Array(nb).fill(0);
  lens.forEach((l) => hist[Math.min(nb - 1, Math.floor(l / 25))]++);
  const histLabels = hist.map((_, i) => `${i * 25}`);

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', background: TH.bg, fontFamily: 'inherit' }}>
      <div style={{ padding: '8px 20px', borderBottom: `1px solid ${TH.headerBorder}`, display: 'flex', gap: 12, alignItems: 'center' }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: TH.text }}>裂缝反演 · 统计分析</span>
        <span style={{ fontSize: 12, color: TH.textMuted }}>
          {res.summary.n_stages} 段 · {res.summary.n_fractures} 条缝 · 平均拟合 RMS {res.summary.mean_fit_rms_m} m · 总 SRA {res.summary.total_sra_1e4m2} 万m²
        </span>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))', gap: 12, alignContent: 'start' }}>
        <div style={PANEL}><div style={HDR}>每段平均缝长 (m)</div><BarChart labels={labels} values={meanLen} color={TH.bar2} ylabel="m" meanLine /></div>
        <div style={PANEL}><div style={HDR}>每段改造面积 SRA (万m²)</div><BarChart labels={labels} values={sra} color={TH.bar} ylabel="万m²" /></div>
        <div style={PANEL}><div style={HDR}>缝面走向玫瑰图 (共 {strikes.length} 条)</div><RoseChart strikes={strikes} /></div>
        <div style={PANEL}><div style={HDR}>每段反演缝面数</div><BarChart labels={labels} values={nfr} color={TH.accent} ylabel="条" /></div>
        <div style={PANEL}><div style={HDR}>缝长分布直方图 (25m/格)</div><BarChart labels={histLabels} values={hist} color={TH.bar} ylabel="条数" /></div>
        <div style={{ ...PANEL, padding: 12, fontSize: 12, color: TH.textMuted, lineHeight: 1.7 }}>
          与 MATLAB 参考实现同源的统计口径:缝长 = 缝面多边形最远点对距离;SRA = 每段事件点凸包面积;
          走向 = RANSAC 拟合直线方位(0°=正北, 双向对称)。缝高与 SRV 由「压裂模拟」P3D 正演联合估计。
        </div>
      </div>
    </div>
  );
}
